/**
 * 파일 경로 관련 유틸리티 함수
 * GCS 경로 정규화 및 파일 경로 처리
 */

import type { FileData, NormalizedFilePath } from "@/types/file";

/**
 * GCS 경로를 정규화하는 함수
 * 여러 파일에서 중복 사용되던 경로 정규화 로직을 통합
 *
 * @param path - 정규화할 경로 (gs:// 형식 또는 내부 경로)
 * @param uid - 사용자 UID (선택사항)
 * @returns 정규화된 경로
 */
export const normalizeFilePath = (
  path: string | null | undefined,
  uid?: string,
): string | null => {
  if (!path) return null;

  // gs://로 시작하는 경우 버킷 내부 경로로 변환
  let internalPath = path;
  if (typeof path === "string" && path.startsWith("gs://")) {
    const parts = path.split("/");
    if (parts.length >= 4) {
      // gs://bucket-name/uid/... 형식에서 uid/... 부분만 추출
      internalPath = parts.slice(3).join("/");
    }
  }

  // 경로 정규화: 잘못된 경로 형식 수정
  // 예: uid/ForkLift.viewr/ForkLift.viewr.viewr -> uid/ForkLift/ForkLift.viewr
  const pathParts = internalPath.split("/");
  if (pathParts.length >= 3) {
    const folderName = pathParts[1];
    const fileName = pathParts.slice(2).join("/");

    // 폴더명이 .viewr로 끝나고 파일명이 .viewr.viewr로 끝나는 경우 수정
    if (folderName.endsWith(".viewr") && fileName.endsWith(".viewr.viewr")) {
      const baseName = folderName.slice(0, -6); // .viewr 제거
      internalPath = `${pathParts[0]}/${baseName}/${baseName}.viewr`;
      console.log(`[filePathUtils] 경로 자동 수정: ${path} -> ${internalPath}`);
    }
    // 폴더명이 .viewr로 끝나고 파일명이 .viewr로 끝나는 경우
    else if (folderName.endsWith(".viewr") && fileName.endsWith(".viewr")) {
      const baseName = folderName.slice(0, -6); // .viewr 제거
      const fileBase = fileName.slice(0, -6); // .viewr 제거
      // 파일명의 base와 폴더명의 base가 같으면 정규화
      if (baseName === fileBase) {
        internalPath = `${pathParts[0]}/${baseName}/${baseName}.viewr`;
        console.log(
          `[filePathUtils] 경로 자동 수정: ${path} -> ${internalPath}`,
        );
      }
    }
  }

  // gs:// 형식으로 반환
  if (path.startsWith("gs://")) {
    const bucketMatch = path.match(/^gs:\/\/([^/]+)/);
    if (bucketMatch) {
      return `gs://${bucketMatch[1]}/${internalPath}`;
    }
  }

  // 내부 경로만 있는 경우
  if (!internalPath.includes("/") && uid) {
    // 파일명만 있는 경우 uid/파일명 형식으로 변환
    return `${uid}/${internalPath}`;
  }

  return internalPath;
};

/**
 * Rename API 호출 시 전달할 경로로 정규화 (Home/Editor 동일 로직)
 * @param path - gcs_path, full_path 또는 viewrGCSPath
 * @param uid - 사용자 UID (선택)
 * @returns 정규화된 경로 (gs:// 또는 object path)
 */
export const getPathForRenameApi = (
  path: string | null | undefined,
  uid?: string,
): string | null => normalizeFilePath(path, uid);

/**
 * 파일 데이터에서 최종 파일 경로를 추출
 * HOME.jsx와 RecentFileCard.jsx에서 중복 사용되던 로직 통합
 *
 * @param file - 파일 데이터 객체
 * @param uid - 사용자 UID (선택사항)
 * @returns 최종 파일 경로
 */
export const getFinalFilePath = (
  file: FileData | null | undefined,
  uid?: string,
): string | null => {
  if (!file) return null;

  const p = file.gcs_path || file.full_path || file.name || file.filename || "";

  if (!p) return null;

  // gs:// 경로는 그대로 사용
  if (typeof p === "string" && p.startsWith("gs://")) {
    return p;
  }

  // 새 규칙: 파일명만 주어진 경우 uid/<basename>/<basename>.viewr로 유도
  if (!p.includes("/")) {
    const base = p.toLowerCase().endsWith(".viewr") ? p.slice(0, -6) : p;
    return uid ? `${uid}/${base}/${base}.viewr` : `${base}/${base}.viewr`;
  }

  return p;
};

/**
 * 파일 경로를 정규화하고 상세 정보를 반환
 *
 * @param path - 정규화할 경로
 * @param uid - 사용자 UID (선택사항)
 * @returns 정규화된 경로 정보
 */
export const normalizeFilePathDetailed = (
  path: string | null | undefined,
  uid?: string,
): NormalizedFilePath | null => {
  if (!path) return null;

  const isGCS = typeof path === "string" && path.startsWith("gs://");
  const bucketMatch = isGCS ? path.match(/^gs:\/\/([^/]+)/) : null;
  const bucket = bucketMatch ? bucketMatch[1] : undefined;

  // 내부 경로 추출
  let internalPath = path;
  if (isGCS) {
    const parts = path.split("/");
    if (parts.length >= 4) {
      internalPath = parts.slice(3).join("/");
    }
  }

  // 경로 정규화 적용
  const normalized = normalizeFilePath(path, uid) || internalPath;

  return {
    original: path,
    normalized,
    bucket,
    internalPath,
    isGCS: !!isGCS,
  };
};

/**
 * 파일 데이터에서 경로 추출 (여러 가능한 필드에서 시도)
 *
 * @param fileData - 파일 데이터 객체
 * @returns 추출된 파일 경로
 */
export const extractFilePath = (
  fileData: FileData | null | undefined,
): string | null => {
  if (!fileData) return null;

  return (
    fileData.gcs_path ||
    fileData.full_path ||
    fileData.name ||
    fileData.filename ||
    null
  );
};

/**
 * 파일 데이터에서 파일명 추출
 *
 * @param fileData - 파일 데이터 객체
 * @param fallbackName - 대체 파일명
 * @returns 추출된 파일명
 */
export const extractFileName = (
  fileData: FileData | null | undefined,
  fallbackName: string = "file",
): string => {
  const name =
    fileData?.title || fileData?.filename || fileData?.name || fallbackName;

  // .viewr 확장자 제거 (필요시 ensureViewrExtension에서 다시 추가됨)
  return name.endsWith(".viewr") ? name.slice(0, -6) : name;
};

/** 기본 썸네일 이미지 경로: viewr-samples-[env] 버킷 내 Base/BaseThumbnail.png */
const DEFAULT_THUMB_OBJECT_PATH = "Base/BaseThumbnail.png";

const PLACEHOLDER_THUMB_PATH_MARKERS = [
  DEFAULT_THUMB_OBJECT_PATH,
  "BaseThumbnail.png",
] as const;

/** API signed URL / thumbnail_path 가 샘플 기본 썸네일(placeholder)인지 판별 */
export const isPlaceholderProjectThumbnail = (
  signedUrl?: string | null,
  thumbnailPath?: string | null,
): boolean => {
  const haystack = `${thumbnailPath ?? ""} ${signedUrl ?? ""}`;
  if (!haystack.trim()) {
    return false;
  }
  return PLACEHOLDER_THUMB_PATH_MARKERS.some((marker) =>
    haystack.includes(marker),
  );
};

/**
 * 파일 버킷 이름에서 샘플 버킷 접미사 추출 (viewr-project-files-dev -> -dev, viewr-vse-files-stg -> -stg)
 */
function getSamplesBucketSuffixFromBucket(fileBucket: string): string {
  const match = fileBucket.match(/-((?:dev|stg|prod))$/);
  return match ? `-${match[1]}` : "";
}

/**
 * gcs_path에서 환경을 알 수 없을 때 REACT_APP_API_URL에서 환경 추론 (새로 만든 .viewr 등)
 * viewrapi-dev-... -> -dev, viewrapi-stg / viewrapi-stage -> -stg, viewrapi-prod -> -prod
 */
function getSamplesBucketSuffixFromApiUrl(): string {
  const url =
    (typeof process !== "undefined" && process.env?.REACT_APP_API_URL) || "";
  if (/viewrapi-dev|[-.]dev\./.test(url)) return "-dev";
  if (/viewrapi-stg|viewrapi-stage|[-.]stg\./.test(url)) return "-stg";
  if (/viewrapi-prod|[-.]prod\./.test(url)) return "-prod";
  return "";
}

/** VSE 파일용 기본 버킷 (Library Import objects 변환 결과 등) */
const VSE_BUCKET_BASE = "viewr-vse-files";

/** ViewR(.viewr, .png) 파일용 기본 버킷 */
const PROJECT_FILES_BUCKET_BASE = "viewr-project-files";

/**
 * Save 시 ViewR 경로 정규화 (trailing slash 제거, 이중 확장자 수정)
 * flat→folder 변환은 하지 않음 (기존 blob이 flat 경로에 존재할 수 있음)
 *
 * @param path - gcs_path 또는 viewrGCSPath
 * @param uid - 사용자 UID (선택, normalizeFilePath에 전달)
 * @returns 정규화된 경로
 */
export const ensureViewrPathStructure = (
  path: string | null | undefined,
  uid?: string,
): string => {
  if (!path || typeof path !== "string") return "";
  // trailing slash 제거 — GCS 폴더 오인 방지
  let result = path.replace(/\/+$/, "").trim();
  if (!result) return "";
  // 이중 확장자 수정 (uid/Folder.viewr/Folder.viewr.viewr → uid/Folder/Folder.viewr)
  const normalized = normalizeFilePath(result, uid);
  return normalized ?? result;
};

/**
 * object path만 있는 경우 스토리지 URI 형식으로 변환
 * update-viewr API 등은 full storage URI를 기대하므로, object path만 전달되면
 * 백엔드가 첫 세그먼트(uid)를 버킷으로 잘못 해석하여 "Invalid bucket name" 오류 발생
 *
 * .viewr 경로는 viewr-project-files-* 버킷, .vse 등은 viewr-vse-files-* 버킷 사용
 *
 * @param path - gs://bucket/path, s3://bucket/path 또는 object path
 * @returns 이미 완전한 storage URI면 그대로, 아니면 legacy gs://bucket/path 형식
 */
export const ensureFullGcsPathForVse = (
  path: string | null | undefined,
): string => {
  if (!path || typeof path !== "string") return "";
  // AWS migration: a complete s3:// URI must never be treated as an object
  // key. Doing so previously produced gs://<bucket>/s3://<bucket>/... on Save.
  if (path.startsWith("gs://") || path.startsWith("s3://")) return path;
  const suffix = getSamplesBucketSuffixFromApiUrl() || "-dev";
  const objectPath = path.replace(/^\//, "");
  // .viewr 경로는 viewr-project-files 버킷 사용 (.viewr, .png 저장 위치)
  const isViewrPath =
    objectPath.toLowerCase().includes(".viewr") ||
    objectPath.toLowerCase().endsWith(".png");
  const bucket = isViewrPath
    ? `${PROJECT_FILES_BUCKET_BASE}${suffix}`
    : `${VSE_BUCKET_BASE}${suffix}`;
  return `gs://${bucket}/${objectPath}`;
};

/**
 * 기본 썸네일 URL 반환
 * 기본 썸네일은 viewr-samples-[개발환경]/Base/BaseThumbnail.png (예: viewr-samples-dev) 에 두는 것을 전제로,
 * 1) 파일의 gcs_path 버킷에서 환경(dev/stg/prod) 추출, 2) 없으면 REACT_APP_API_URL에서 추론.
 *
 * @param gcsPath - 파일의 GCS 경로 (gs://bucket/path/...). 있으면 버킷에서 환경 추출
 * @returns 기본 썸네일 스토리지 URL (비공개이므로 실제 표시는 Signed URL로 요청해 사용)
 */
export const getDefaultThumbnailUrl = (
  gcsPath: string | null | undefined,
): string => {
  let suffix = "";
  if (gcsPath && typeof gcsPath === "string" && gcsPath.startsWith("gs://")) {
    const withoutScheme = gcsPath.slice("gs://".length);
    const firstSlash = withoutScheme.indexOf("/");
    if (firstSlash >= 0) {
      const bucket = withoutScheme.slice(0, firstSlash);
      suffix = getSamplesBucketSuffixFromBucket(bucket);
    }
  }
  if (!suffix) {
    suffix = getSamplesBucketSuffixFromApiUrl();
  }
  const samplesBucket = `viewr-samples${suffix}`;
  return `https://storage.googleapis.com/${samplesBucket}/${DEFAULT_THUMB_OBJECT_PATH}`;
};

/**
 * GCS 경로에서 썸네일 PNG URL 파생 (.viewr → 같은 경로의 .png)
 */
export const deriveViewrPngFromGcs = (
  gcsPath: string | null | undefined,
): string | null => {
  try {
    if (!gcsPath || typeof gcsPath !== "string") return null;
    if (!gcsPath.startsWith("gs://")) return null;
    const withoutScheme = gcsPath.slice("gs://".length);
    const firstSlash = withoutScheme.indexOf("/");
    if (firstSlash < 0) return null;
    const bucket = withoutScheme.slice(0, firstSlash);
    const objectPath = withoutScheme.slice(firstSlash + 1);
    const lastDot = objectPath.lastIndexOf(".");
    if (lastDot < 0) return null;
    const pngPath = objectPath.slice(0, lastDot) + ".png";
    return `https://storage.googleapis.com/${bucket}/${pngPath}`;
  } catch (_) {
    return null;
  }
};
